import React from 'react';
import { Box, Grid, Typography, Divider, Button, useTheme, useMediaQuery } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import { ReporteConsolidado } from '../../../types/consolidatedReport.types';
import ReportSummaryCards from './ReportSummaryCards';
import CategoryRadarChart from './CategoryRadarChart';
import CategoryBarsChart from './CategoryBarsChart';
import TrendLineChart from './TrendLineChart';
import HeatmapTable from './HeatmapTable';
import AspectosAMejorar from './AspectosAMejorar';
import Fortalezas from './Fortalezas';
import AuditoriasTable from './AuditoriasTable';

interface ReportResultsProps {
  data: ReporteConsolidado;
  onDownloadPDF: () => void;
  onDownloadExcel: () => void;
  downloading: boolean;
  downloadingExcel: boolean;
}

const ReportResults: React.FC<ReportResultsProps> = ({ data, onDownloadPDF, onDownloadExcel, downloading, downloadingExcel }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Encabezado del Reporte con botón PDF */}
      <Box sx={{ 
        mb: 3, 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'flex-start',
        gap: isMobile ? 2 : 0
      }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 700, mb: 1 }}>
            {data.resumen_general.negocio.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data.resumen_general.negocio.address}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <strong>Periodo:</strong> {data.resumen_general.periodo.descripcion}
          </Typography>
        </Box>
        
        {/* Botón Descargar PDF */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: isMobile ? 'stretch' : 'flex-end' }}>
          <Button
            variant="contained"
            color="error"
            startIcon={<PictureAsPdfIcon />}
            onClick={onDownloadPDF}
            disabled={downloading || downloadingExcel}
            fullWidth={isMobile}
            sx={{ minWidth: isMobile ? '100%' : 160 }}
          >
            {downloading ? 'Generando PDF...' : 'Descargar PDF'}
          </Button>
          <Button
            variant="outlined"
            color="success"
            startIcon={<TableChartIcon />}
            onClick={onDownloadExcel}
            disabled={downloading || downloadingExcel}
            fullWidth={isMobile}
            sx={{ minWidth: isMobile ? '100%' : 160 }}
          >
            {downloadingExcel ? 'Generando Excel...' : 'Exportar Excel'}
          </Button>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Tarjetas de Resumen (KPIs) */}
      <ReportSummaryCards resumen={data.resumen_general} />

      {/* Gráficos Principales */}
      <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: 3 }}>
        {/* Radar de Categorías - Ancho completo */}
        <Grid item xs={12}>
          <CategoryRadarChart categorias={data.por_categoria} />
        </Grid>
      </Grid>

      {/* Gráfico de Barras por Categoría */}
      <Box sx={{ mb: 3 }}>
        <CategoryBarsChart categorias={data.por_categoria} />
      </Box>

      {/* Tendencia por Auditoría */}
      {data.auditorias_detalle.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <TrendLineChart auditorias={data.auditorias_detalle} timezone={data.resumen_general.negocio?.timezone} />
        </Box>
      )}

      {/* Aspectos a Mejorar */}
      <AspectosAMejorar data={data.aspectos_mejorar} />

      {/* Fortalezas */}
      <Fortalezas data={data.fortalezas} />

      {/* Mapa de Calor */}
      {data.heatmap_data && data.heatmap_data.audits && data.heatmap_data.audits.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <HeatmapTable data={data.heatmap_data} />
        </Box>
      )}

      {/* Tabla de Auditorías */}
      <AuditoriasTable auditorias={data.auditorias_detalle} timezone={data.resumen_general.negocio?.timezone} />
    </Box>
  );
};

export default ReportResults;
